import { useRef, useCallback, useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { driver } from 'driver.js';
import { useConfig } from '@rs-core/hooks';
import { extractDataTutValue, isElementNotCovered } from './utils';
import { getCustomizeToolbarConfig } from './getCustomizeToolbarConfig';
import { getCustomizeToolbarOldMenuConfig } from './getCustomizeToolbarOldMenuConfig';
import { getNewObliquityLineDotConfig } from './getNewObliquityLineDotConfig';
import { getQuadZoomConfig } from './getQuadZoomConfig';
import { getPatientChartConfig } from './getPatientChartConfig';
import { useTutorialStore } from './useTutorialStore';
import { useTutorialStoreShared } from './useTutorialStoreShared';
import { availableTutorials } from './availableTutorials';

import 'driver.js/dist/driver.css';
import './driver-override.css';
import { useBooleanFlagValue } from '@rs-core/hooks/useFlags';

const tutorialConfigs = {
	quadZoom: getQuadZoomConfig(),
	newObliquityLineDot: getNewObliquityLineDotConfig(),
	customizeToolbar: getCustomizeToolbarConfig(),
	customizeToolbarOldMenu: getCustomizeToolbarOldMenuConfig(),
	patientChart: getPatientChartConfig(),
};

export function useTutorial(tutorialName) {
	const __config = useConfig();
	const wonIvTutorial = useBooleanFlagValue('WON-IV-TUTORIAL');

	const { t, ready } = useTranslation('tutorials');

	const isSharedStudy = new URLSearchParams(window.location.search).get('SharedStudy') === 'true';

	const isSharedStoreInitialized = useTutorialStoreShared(state => state.isInitialized);
	const isStoreInitialized = useTutorialStore(state => state.isInitialized);
	const tutorial = useTutorialStore(state => state.getTutorial({ tutorialName }));
	const tutorialState = useTutorialStoreShared(state => state.getTutorialState(tutorialName));
	const { tutorialStateFetched } = useTutorialStoreShared(state => state.flags);
	const queue = useTutorialStore(state => state.queue);
	const { fetchTutorialState, setTutorialState, persistTutorialState, resetTutorialShared } =
		useTutorialStoreShared();
	const { pushToQueue, popFromQueue, isAttribBound, bindAttrib, resetTutorial } = useTutorialStore();

	const actions = {
		pushToQueue,
		popFromQueue,
		fetchTutorialState,
		setTutorialState,
		persistTutorialState,
		isAttribBound,
		bindAttrib,
	};

	const driverObj = useRef();
	const handleMouseEnterRef = useRef(null);

	const [stepsReached, setStepsReached] = useState(0);

	/**
	 *  flag to enable/disable the tutorial
	 */
	const isEnabled = useMemo(() => wonIvTutorial && !isSharedStudy, [wonIvTutorial]);

	/**
	 * flag to force show tutorial for testing
	 */
	const forceTutorialForTesting = useMemo(() => localStorage.getItem('forceShowTutorial') === 'true', []);

	/**
	 * current tutorial config object
	 */
	const tutorialConfig = useMemo(() => {
		if (tutorialName && Object.values(availableTutorials).includes(tutorialName)) {
			return tutorialConfigs[tutorialName](t);
		}
		return null;
	}, [t, tutorialName]);

	const activeTutorialName = useMemo(() => queue[0], [queue?.length]);
	const isActive = useMemo(() => activeTutorialName === tutorialName, [activeTutorialName, tutorialName]);

	const isDriveStarted = useMemo(() => tutorialState?.isDriveStarted, [tutorialState]);
	const setIsDriveStarted = useCallback(
		isStarted => {
			actions.setTutorialState(tutorialName, { ...tutorialState, isDriveStarted: isStarted });
		},
		[actions, tutorialName, tutorialState]
	);

	const isDriveClosed = useMemo(() => tutorialState?.isDriveClosed, [tutorialState]);
	const setIsDriveClosed = useCallback(
		isClosed => {
			actions.setTutorialState(tutorialName, { ...tutorialState, isDriveClosed: isClosed });
		},
		[actions, tutorialName, tutorialState]
	);

	const [isDriveOnRetry, setIsDriveOnRetry] = useState(false);

	const computeTutorialState = useCallback(() => {
		if (!isInitialized) return null;
		const totalSteps = tutorialConfig?.steps.length;
		let steps = stepsReached;
		if (tutorialState?.stepsReached) {
			steps = Math.max(tutorialState.stepsReached, stepsReached);
		}
		let status;
		if (steps >= totalSteps) {
			status = 'completed';
		} else if (isDriveClosed) {
			status = 'skipped';
		} else if (isDriveStarted) {
			status = 'started';
		} else if (isDriveReady) {
			status = 'ready';
		}

		return {
			...tutorialState,
			status,
			stepsReached: Math.min(steps, totalSteps),
			totalSteps,
		};
	}, [
		isInitialized,
		tutorialConfig?.steps.length,
		stepsReached,
		isDriveClosed,
		isDriveStarted,
		isDriveReady,
		tutorialState,
	]);

	/**
	 * Driver object for the tutorial
	 */
	const tutorialDriver = useMemo(() => {
		if (ready && tutorial && isActive && !driverObj.current) {
			driverObj.current = driver({
				...tutorialConfigs[tutorialName](t),
				onNextClick: (element, step) => {
					setStepsReached(step.stepNum || 0);
					if (driverObj.current.hasNextStep()) {
						driverObj.current.moveNext();
					} else {
						driverObj.current.destroy();
					}
				},
				onCloseClick: (element, step) => {
					setStepsReached(step.stepNum || 0);
					driverObj.current.destroy();
				},
				onDestroyed: (element, step) => {
					setStepsReached(step.stepNum || 0);
					setIsDriveClosed(true);
				},
			});
		}
		return driverObj.current;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isActive, ready, tutorial, t]);

	/**
	 * tutorials is iniitialized now and components can start using it's api
	 * primarily data-tut attributes can be extracted and used
	 */
	const storesInitialized = useMemo(() => {
		if (isSharedStoreInitialized && isStoreInitialized) {
			return isSharedStoreInitialized() && isStoreInitialized();
		}
		return false;
	}, [isSharedStoreInitialized, isStoreInitialized]);

	const isInitialized = useMemo(
		() => isEnabled && tutorial && storesInitialized && (forceTutorialForTesting || tutorialStateFetched),
		[forceTutorialForTesting, tutorial, tutorialStateFetched, isEnabled, storesInitialized]
	);

	/**
	 * Moves the tutorial to the next step
	 */
	const moveNext = useCallback(() => {
		if (isInitialized && isDriveStarted) {
			setTimeout(() => {
				tutorialDriver?.moveNext();
			}, 600);
		}
	}, [isDriveStarted, isInitialized, tutorialDriver]);

	/**
	 * Moves the tutorial to the given step index
	 */
	const moveTo = useCallback(
		stepIndex => {
			if (isInitialized && isDriveStarted) {
				setTimeout(() => {
					tutorialDriver?.moveTo(stepIndex);
				}, 600);
			}
		},
		[isDriveStarted, isInitialized, tutorialDriver]
	);

	/**
	 * Recomputes the popover overlay svg with cutout
	 */
	const refresh = useCallback(() => {
		if (isInitialized && isDriveStarted) {
			tutorialDriver?.refresh();
		}
	}, [isDriveStarted, isInitialized, tutorialDriver]);

	/**
	 * Extracts the data-tut attribute value for the given step index
	 * It is responsibility of integrator to bind the right component and step index
	 * Refering the config object is the best way to do it
	 */
	const getTutorialStepAttrib = useCallback(
		stepIndex => {
			if (isInitialized) {
				const elementSelector = tutorialConfig.steps[stepIndex - 1].element;
				const attrib = extractDataTutValue(elementSelector);
				if (attrib) {
					actions.bindAttrib(tutorialName, attrib);
					return attrib;
				}
			}
			return null;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isInitialized, tutorialConfig]
	);

	/**
	 * Checks if the tutorial is not shown already to current user
	 * In testing mode, it will always return true per window refresh
	 */
	const isNotShownAlready = useCallback(() => {
		if (!tutorialState) return true;
		if (tutorialState.isDriveStarted) return false;
		if (forceTutorialForTesting) return true;
		const { status } = tutorialState;
		return status !== 'completed' && status !== 'skipped' && status !== 'started';
	}, [forceTutorialForTesting, tutorialState]);

	/**
	 * Checks if all valid attribs are extracted atleast once by Integrator
	 */
	const allStepsBound = useCallback(() => {
		const totalBoundAttribs = Object.keys(tutorial.boundStepAttribs).length;
		const stepSet = new Set(tutorialConfig.steps || []);
		const totalAttribs = [...stepSet]
			.map(step => (!step.async ? step.element : null))
			.filter(attrib => !!attrib).length;
		return totalBoundAttribs === totalAttribs;
	}, [tutorial?.boundStepAttribs, tutorialConfig]);

	/**
	 * Checks if the tutorial is ready to be driven
	 */
	const isDriveReady = useMemo(() => {
		if (!isInitialized || !tutorialDriver) return false;
		return allStepsBound() && isNotShownAlready();
	}, [allStepsBound, isInitialized, isNotShownAlready, tutorialDriver]);

	/**
	 * Integrator need to call this function when the tutorial meets the eligibility criteria
	 */
	const drive = useCallback(() => {
		if (!queue.includes(tutorialName) && !isDriveStarted && !isDriveClosed && !isDriveOnRetry && isEnabled) {
			actions.pushToQueue(tutorialName);
		}
	}, [queue?.length, tutorialName, isDriveStarted, isDriveClosed, isDriveOnRetry, isEnabled, actions]);

	/**
	 * This useEffect is responsible for starting the tutorial drive and
	 * retry mechanism to drive tutorial again once the element is visible
	 * also it should not block other eligible tutorials till it gets drivable
	 */
	useEffect(() => {
		if (isActive && isDriveReady && !isDriveStarted && !isDriveClosed) {
			const firstElSelector = tutorialConfig?.steps[0].element;
			try {
				const firstEl = document.querySelector(firstElSelector);
				const { result } = isElementNotCovered(firstEl);
				if (result) {
					if (isDriveOnRetry) {
						firstEl.removeEventListener('mouseenter', handleMouseEnterRef.current);
						setIsDriveOnRetry(false);
					}
					setIsDriveStarted(true);
					requestAnimationFrame(() => {
						tutorialDriver?.drive();
					});
				} else if (!isDriveOnRetry) {
					setIsDriveOnRetry(true);
					if (!handleMouseEnterRef.current) {
						handleMouseEnterRef.current = function () {
							actions.pushToQueue(tutorialName);
							setIsDriveOnRetry(false);
						};
					}
					actions.popFromQueue();
					firstEl.removeEventListener('mouseenter', handleMouseEnterRef.current);
					firstEl.addEventListener('mouseenter', handleMouseEnterRef.current);
				}
			} catch (e) {
				console.error('Error in tutorial drive:', e);
			}
		}
	}, [
		isActive,
		isDriveReady,
		isDriveStarted,
		setIsDriveStarted,
		setIsDriveOnRetry,
		tutorialDriver,
		tutorialConfig?.steps,
		isDriveClosed,
	]);

	/**
	 * This useEffect is responsible for fetching the tutorial state from the server
	 */
	useEffect(() => {
		if (!isEnabled) return;
		if (__config && !tutorialStateFetched && !forceTutorialForTesting) {
			actions.fetchTutorialState(__config, tutorialName);
		} else if (forceTutorialForTesting && !tutorialState) {
			actions.setTutorialState(tutorialName, {});
		}
	}, [__config, actions, forceTutorialForTesting, isEnabled, tutorialName, tutorialState, tutorialStateFetched]);

	/**
	 * This useEffect is responsible for persisting the tutorial state to the server
	 */
	useEffect(() => {
		if (!isInitialized) return;
		if (isDriveClosed && stepsReached > 0) {
			const newState = computeTutorialState();
			if (newState) {
				actions.setTutorialState(tutorialName, newState);
			}
			actions.persistTutorialState(__config, tutorialName);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDriveClosed, isDriveStarted, isInitialized, stepsReached]);

	useEffect(() => {
		const steps = Math.max(tutorialState?.stepsReached || 0, stepsReached);
		if (queue.includes(tutorialName) && isDriveClosed && steps > 0) {
			actions.popFromQueue(tutorialName);
		}
	}, [isDriveClosed, actions, activeTutorialName, tutorialName, stepsReached]);

	const resetTutorialStores = useCallback(() => {
		resetTutorial();
		resetTutorialShared();
	}, [resetTutorial, resetTutorialShared]);

	return {
		isInitialized,
		getTutorialStepAttrib,
		drive,
		moveNext,
		moveTo,
		refresh,
		resetTutorialStores,
	};
}

export default useTutorial;
